import React, { useState, useEffect } from "react";
import BodyContainer from "../components/BodyContainer";
import BottomContainer from "../components/BottomContainer";
import Footer from '../components/Footer';
import FooterLinks from "../components/FooterLinks";
import { Helmet } from "react-helmet-async";
import bannerImage from '../assets/banner4.png';
import Banner from '../components/Banner';
import { useNavigate } from "react-router-dom";
import ScrollingBanner from "../components/scrollingBanner";
import '../Cart.css';
import '../Site.css'; 
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

function Cart() {
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountError, setDiscountError] = useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [userState, setUserState] = useState("");
  const [showStateModal, setShowStateModal] = useState(false);
  const [taxAmount, setTaxAmount] = useState(0);
  const [discountMessage, setDiscountMessage] = useState("");
  const [referralIdUsed, setReferralIdUsed] = useState(null);
  const [isFreeOrder, setIsFreeOrder] = useState(false);

  const SPECIAL_CODES = {
  SU2026ksdjfjggkgkgkkggk: {
    type: "free",
    exactTotal: 35
  },
  SU2026P: {
    type: "free",
    requiredProductId: "PROD8", // 👈 replace with real product ID
    productName: "Pharmacy Practice, Management, and Leadership Study Guide"

  },
  HALFPRICE50: {
    type: "percentage",
    value: 50,
    minTotal: 50
  }
};


  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }

    const user = JSON.parse(storedUser);
    if (!user.State) {
      setShowStateModal(true);
    } else {
      setUserState(user.State);
    }

    if (!user.Cart || user.Cart.length === 0) {
      setCart([]);
      return;
    }

    setCart(user.Cart);
    fetchCartProducts(user.Cart);
  }, [navigate]);

  const handleStateSubmit = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user.Id;

    const response = await fetch(`${API_URL}/api/user/state`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, state: userState })
    });

    if (response.ok) {
      user.State = userState;
      localStorage.setItem("user", JSON.stringify(user));
      setShowStateModal(false);
    }
  };

  const getPayPalEmail = (paypalDetails) => {
  return (
    paypalDetails?.payer?.email_address ||
    paypalDetails?.payer?.email ||
    paypalDetails?.payment_source?.paypal?.email_address ||
    paypalDetails?.purchase_units?.[0]?.payee?.email_address ||
    null
  );
};

const getPayPalPhone = (paypalDetails) => {
  return (
    paypalDetails?.payer?.phone?.phone_number?.national_number ||
    paypalDetails?.payer?.phone?.national_number ||
    paypalDetails?.payment_source?.card?.billing_address?.phone ||
    null
  );
};

const getPayPalPaymentInfo = (paypalDetails) => ({
  paypalOrderId: paypalDetails?.id || null,
  paypalCaptureId:
    paypalDetails?.purchase_units?.[0]?.payments?.captures?.[0]?.id || null,
  paypalMerchantId:
    paypalDetails?.purchase_units?.[0]?.payee?.merchant_id || null,
  paypalMerchantEmail:
    paypalDetails?.purchase_units?.[0]?.payee?.email_address || null,
});

  async function fetchCartProducts(cartItems) {
    try {
      const response = await fetch(`${API_URL}/api/products`);
      const data = await response.json();
      const filteredProducts = data.filter((product) => cartItems.includes(product.Id));

      const productsWithURLs = await Promise.all(
        filteredProducts.map(async (product) => {
          let fileURL = null;
          if (product.ProductType === "Course") {
            try {
              const previewRes = await fetch(`${API_URL}/api/courses/by-product/${product.Id}`);
              const previewData = await previewRes.json();
              fileURL = previewData.course?.FreeFileURL || null;
            } catch (err) {
              console.error(`Failed to fetch preview for ${product.Id}:`, err);
            }
          }
          return { ...product, FileURL: fileURL };
        })
      );

      setProducts(productsWithURLs);

      const initialSelection = {};
      productsWithURLs.forEach((product) => {
        if (product.PriceOptions) {
          const defaultDuration = Object.keys(product.PriceOptions)[0];
          initialSelection[product.Id] = defaultDuration;
        }
      });
      setSelectedOptions(initialSelection);
    } catch (error) {
      console.error("Error fetching cart products:", error);
    }
  }

  const handleSelectionChange = (productId, selectedDuration) => {
    setSelectedOptions((prevOptions) => ({ ...prevOptions, [productId]: selectedDuration }));
  };

const handleRemove = async (productId) => {
  try {
    const updatedCart = cart.filter((id) => id !== productId);
    setCart(updatedCart);
    setProducts(products.filter((product) => product.Id !== productId));

    // ✅ Reset discount state immediately (prevents negative totals)
    setDiscountAmount(0);
    setDiscountCode("");
    setIsFreeOrder(false);
    setDiscountMessage("");
    setDiscountError("");

    const storedUser = JSON.parse(localStorage.getItem("user"));

    const response = await fetch(`${API_URL}/api/cart/remove`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: storedUser.Id, productId }),
    });

    if (!response.ok) throw new Error("Failed to remove product from cart");

    storedUser.Cart = updatedCart;
    localStorage.setItem("user", JSON.stringify(storedUser));

    window.dispatchEvent(new Event("cartUpdated"));

    // 🔄 Fallback reload ONLY if cart still has items
    if (updatedCart.length > 0) {
      setTimeout(() => {
        window.location.reload();
      }, 200);
    }

  } catch (error) {
    console.error("Error removing item from cart:", error);
  }
};

  useEffect(() => {
    const subtotal = products.reduce((sum, product) => {
      const selectedDuration = selectedOptions[product.Id];
      const price = product.PriceOptions?.[selectedDuration] || 0;
      return sum + parseFloat(price);
    }, 0);

const taxRateByState = {
  "NJ": 0.06625
};

    const taxRate = taxRateByState[userState] || 0;
    const tax = parseFloat((subtotal * taxRate).toFixed(2));
    setTaxAmount(tax);
    setTotalPrice(subtotal - discountAmount + tax);
  }, [selectedOptions, products, discountAmount, userState]);

const applyDiscount = async () => {
  const code = discountCode.trim().toUpperCase();

  setDiscountError("");
  setDiscountMessage("");
  setIsFreeOrder(false);

  if (!code) {
    setDiscountError("Please enter a discount code.");
    return;
  }

  const subtotal = products.reduce((sum, product) => {
    const selectedDuration = selectedOptions[product.Id];
    const price = product.PriceOptions?.[selectedDuration] || 0;
    return sum + parseFloat(price);
  }, 0);

  const roundedSubtotal = parseFloat(subtotal.toFixed(2));

  setIsApplyingDiscount(true);

  try {
    const response = await fetch(`${API_URL}/api/referrals/apply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Invalid discount code.");
    }

    // 🔥 =========================
    // 🎓 ONE-TIME CODE HANDLING
    // 🔥 =========================
    if (data.type === "one_time") {
      const hasProduct = products.some(p => p.Id === data.productId);

      if (!hasProduct) {
        setDiscountError(
          `This code requires "${data.productName}" in your cart.`
        );
        return;
      }

      const targetProduct = products.find(p => p.Id === data.productId);
      const selectedDuration = selectedOptions[targetProduct.Id];
      const price = targetProduct.PriceOptions?.[selectedDuration] || 0;

      const discount = parseFloat(price);

      setDiscountAmount(discount);

      const remainingTotal = roundedSubtotal - discount;

      if (remainingTotal <= 0) {
        setIsFreeOrder(true);
        setDiscountMessage("Promo applied! Your order is free.");
      } else {
        setDiscountMessage(`Promo applied! You saved $${discount.toFixed(2)}.`);
      }

      // store code for redeem step later
      setReferralIdUsed(data.code);

      return;
    }

    // 🔁 =========================
    // 💸 NORMAL REFERRAL
    // 🔁 =========================

    if (subtotal < 50) {
      setDiscountError("Cart total must be at least $50 to use a discount code.");
      return;
    }

    const discountValue = parseFloat(data.discount);
    const discountType = data.type || "flat";

    let calculatedDiscount = 0;

    if (discountType === "percentage") {
      calculatedDiscount = (discountValue / 100) * subtotal;
    } else {
      calculatedDiscount = discountValue;
    }

    setDiscountAmount(calculatedDiscount);

    setDiscountMessage(
      `Discount applied! You saved $${calculatedDiscount.toFixed(2)}.`
    );

    setReferralIdUsed(data.referralId);

  } catch (error) {
    setDiscountAmount(0);
    setDiscountError(error.message);
  } finally {
    setIsApplyingDiscount(false);
  }
};

const handleSuccessfulPurchase = async (paypalDetails = null) => {
  try {
    console.log("PayPal capture details:", paypalDetails);

    const storedUser = JSON.parse(localStorage.getItem("user"));
    const userId = storedUser.Id;
    const paypalEmail = getPayPalEmail(paypalDetails);
    const paypalPhone = getPayPalPhone(paypalDetails);
    const paypalInfo = getPayPalPaymentInfo(paypalDetails);



    console.log("Extracted PayPal email:", paypalEmail);

    let expandedSubscriptions = [];

    // Build subscriptions based on cart products
    for (const product of products) {
      const subscriptionLength = parseInt(selectedOptions[product.Id], 10);
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + subscriptionLength);
      const paypalOrderId = paypalDetails?.id || null;

      const paypalCaptureId =
      paypalDetails?.purchase_units?.[0]?.payments?.captures?.[0]?.id || null;

      if (product.ProductType === "Bundle" && Array.isArray(product.ProductsIncluded)) {
        product.ProductsIncluded.forEach((includedProductId) => {
          expandedSubscriptions.push({
            userId,
            products: [includedProductId],
            expirationDate: expirationDate.toISOString(),
            status: "Active",
            paypalEmail,
            paypalPhone,
            ...paypalInfo,
          });
        });
      } else {
        expandedSubscriptions.push({
          userId,
          products: [product.Id],
          expirationDate: expirationDate.toISOString(),
          status: "Active",
          paypalEmail,
          paypalPhone,
          ...paypalInfo,
       });
      }
    }

    // Create subscriptions in backend
    for (const subscription of expandedSubscriptions) {
      await fetch(`${API_URL}/api/subscriptions/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(subscription),
      });
    }

    // 🎓 Redeem one-time referral code AFTER successful purchase
    if (referralIdUsed && isFreeOrder) {
      try {
        const redeemResponse = await fetch(`${API_URL}/api/referrals/redeem`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: referralIdUsed,
            userId: userId // ✅ must match backend casing
          }),
        });

        if (!redeemResponse.ok) {
          const data = await redeemResponse.json();
          console.warn("Redeem code error:", data.error);
        } else {
          console.log("Referral code redeemed successfully.");
        }
      } catch (err) {
        console.error("Error redeeming referral code:", err);
      }
    }

    // 💸 Normal referral increment if not a one-time free order
    else if (referralIdUsed && !isFreeOrder) {
      try {
        await fetch(`${API_URL}/api/referrals/increment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ referralId: referralIdUsed }),
        });
      } catch (err) {
        console.error("Error incrementing referral:", err);
      }
    }

    // ✅ Clear cart after successful purchase
    setCart([]);
    setProducts([]);
    storedUser.Cart = [];
    localStorage.setItem("user", JSON.stringify(storedUser));
    window.dispatchEvent(new Event("cartUpdated"));

    // Notify user once
    alert("Order successful! Your subscriptions have been activated.");

    setTimeout(() => window.location.reload(), 1500);
  } catch (error) {
    console.error("Error processing subscription:", error);
    alert("Error processing your order. Please try again.");
  }
};
  // <ScrollingBanner /> add under helmet when banner is needed

  return (
    <>
      <Helmet><title>PharmPrepPro - Cart</title></Helmet>
      
      <BottomContainer>
        <Banner height="300px" image={bannerImage} />
        <BodyContainer>
          <h1 className="white-text cart-heading">Shopping Cart</h1>

          {showStateModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Please select your state</h3>
                <form onSubmit={handleStateSubmit}>
                  <select value={userState} onChange={(e) => setUserState(e.target.value)} required>
                    <option value="">Select your state</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                  <button type="submit">Save</button>
                </form>
              </div>
            </div>
          )}

          <div className="cart-content-box">
            {products.length === 0 ? (
  <p className="empty-cart-message">Your Cart is Currently Empty</p>
) : (
  <div className="cart-container">
    <ul className="cart-items-list">
      {products.map((product) => (
        <li key={product.Id} className="cart-item">
          <div className="cart-item-content">
            <img
              src={product.PreSignedURL || "/placeholder.png"}
              alt={product.Name}
              className="cart-product-image"
            />
            <div className="cart-item-details">
              <h3 className="cart-product-title">{product.Name}</h3>
              {product.PriceOptions && (
                <label>
                  Subscription Length:{" "}
                  <select
                    value={selectedOptions[product.Id] || ""}
                    onChange={(e) =>
                      handleSelectionChange(product.Id, e.target.value)
                    }
                    className="cart-duration-select"
                  >
                    {Object.entries(product.PriceOptions).map(
                      ([duration, price]) => (
                        <option key={duration} value={duration}>
                          {duration} Days - ${price}
                        </option>
                      )
                    )}
                  </select>
                </label>
              )}
            </div>
          </div>
          <button
            className="remove-button"
            onClick={() => handleRemove(product.Id)}
          >
            Remove
          </button>
        </li>
      ))}
    </ul>
  </div>
)}




            <div className="discount-section">
              <input
                type="text"
                placeholder="Discount Code"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                className="discount-input"
              />
              <button onClick={applyDiscount} className="apply-discount-button" disabled={isApplyingDiscount}>Apply</button>
              
              {discountError && <p className="discount-error">{discountError}</p>}
              {discountMessage && <p className="discount-message">{discountMessage}</p>}
            </div>


            <div className="tax-section">
              <h4>Estimated Tax: ${taxAmount.toFixed(2)}</h4>
            </div>

            <div className="total-price">
              <h3>Total: ${totalPrice.toFixed(2)}</h3>
            </div>

            {products.length > 0 && !isFreeOrder && (
  <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID }}>
    <PayPalButtons
      style={{ layout: "vertical" }}
      forceReRender={[totalPrice]}
      createOrder={(data, actions) => {
        return actions.order.create({
          purchase_units: [
            {
              amount: {
                value: totalPrice.toFixed(2),
              },
            },
          ],
        });
      }}
      onApprove={async (data, actions) => {
        const details = await actions.order.capture();
        handleSuccessfulPurchase(details);
      }}
    />
  </PayPalScriptProvider>
)}
{products.length > 0 && isFreeOrder && totalPrice === 0 && (
  <button
    className="apply-discount-button"
    onClick={handleSuccessfulPurchase}
  >
    Complete Free Order
  </button>
)}
          </div>
        </BodyContainer>
      </BottomContainer>
      <FooterLinks />
      <Footer />
    </>
  );
}

export default Cart;
